import { aggregate, Query, QueryFields, readItems } from "@directus/sdk";
import { GaripalliDirectusClient } from "../directus/client";
import { MatchEvent, MatchEventType } from "../directus/collections";
import { Schema } from "../directus/schema";
import { GaripalliIDB } from "../extensions/idb";
import { isTransientHttpError, withRetry } from "../libs/retry";

export interface FetchEventProps {
  matchId?: string;
  cityEscapeId?: string;
  eventTypes?: MatchEventType[];
  targetId?: string;
  sort?: Query<Schema, MatchEvent>["sort"];
  limit?: number;
  offset?: number;
  fields?: string[];
}

export function fetchEvents(
  client: GaripalliDirectusClient,
  {
    matchId,
    cityEscapeId,
    eventTypes,
    targetId,
    sort = ["match", "-date_created"],
    offset = 0,
    limit = 10000,
    fields = ["*"],
  }: FetchEventProps
) {
  const filter: Record<string, any> = {};

  if (eventTypes) {
    filter.event_type = { _in: eventTypes };
  }

  if (targetId) {
    filter.target_id = { _eq: targetId };
  }

  if (matchId) {
    filter.match = { _eq: matchId };
  }

  return client.request<MatchEvent[]>(
    readItems("match_events", {
      fields,
      filter,
      sort,
      limit,
      offset,
    })
  );
}

export async function fetchAllEvents(
  client: GaripalliDirectusClient,
  props?: Omit<FetchEventProps, "limit">
) {
  // console.log("Fetching all events with props:", props);
  // return []
  let events: MatchEvent[] = [];
  let accumulator: MatchEvent[] = [];
  let offset = 0;
  const limit = 10000;
  do {
    events = await withRetry(
      () =>
        fetchEvents(client, {
          ...(props || {}),
          limit,
          offset,
        }),
      { shouldRetry: isTransientHttpError }
    );
    accumulator.push(...events);
    offset += limit;
  } while (events.length === limit);

  return accumulator;
}

export async function getFetchedEventsTotal(db: GaripalliIDB) {
  const totalEntries = await db.count("timelines");
  const skips = await db
    .get("sync_metadata", "lastSync")
    .then((data) => data?.match_events?.skips || 0);
  return totalEntries + skips;
}

export async function fetchAllEventsAfterGivenDate(
  client: GaripalliDirectusClient,
  date: string,
  {
    fields,
    sort,
  }: {
    fields?: QueryFields<Schema, MatchEvent> | undefined;
    sort?: Query<Schema, MatchEvent>["sort"];
  } = { fields: ["*"], sort: ["date_created"] }
) {
  // console.log("Fetching events after date:", date);
  // return []
  let events: MatchEvent[] = [];
  let accumulator: MatchEvent[] = [];
  let offset = 0;
  const limit = 10000;
  do {
    events = await withRetry(
      () =>
        client.request<MatchEvent[]>(
          readItems("match_events", {
            fields: fields ?? ["*"],
            filter: {
              date_created: {
                _gt: date,
              },
            },
            limit: limit,
            offset: offset,
            sort: sort,
          })
        ),
      { shouldRetry: isTransientHttpError }
    );
    accumulator.push(...events);
    offset += limit;
  } while (events.length === limit);

  return accumulator;
}

export function fetchEventCount(client: GaripalliDirectusClient) {
  return client.request<{ count: number }>(
    aggregate("match_events", {
      aggregate: {
        count: "id",
      },
    })
  );
}

export async function verifyFetchedEventConsistency(
  client: GaripalliDirectusClient,
  totalFetchedEvents: number,
  lastEventId: string
) {
  if (totalFetchedEvents === 0) {
    console.warn(`No events fetched after lastEventId: ${lastEventId}`);
    return false;
  }

  const results = await fetchEvents(client, {
    limit: 1,
    fields: ["id"],
    offset: totalFetchedEvents - 1,
    sort: ["date_created"],
  });

  // console.log(
  //   `Verifying consistency: totalFetchedEvents=${totalFetchedEvents}, lastEventId=${lastEventId}, fetchedEventId=${results[0]?.id}`
  // );

  return results.length > 0 && results[0].id === lastEventId;
}
